import React, { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { stopImpersonation } from "../store/slices/impersonationSlice";

const formatRemaining = (expiresAtIso: string | null): string => {
  if (!expiresAtIso) return "";
  const ms = new Date(expiresAtIso).getTime() - Date.now();
  if (ms <= 0) return "00:00";
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60)
    .toString()
    .padStart(2, "0");
  const s = (totalSec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

const ImpersonationBanner: React.FC = () => {
  const dispatch = useAppDispatch();
  const { active, targetBrandName, targetUserEmail, expiresAt } =
    useAppSelector((state) => state.impersonation);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [active]);

  useEffect(() => {
    if (active && expiresAt && new Date(expiresAt).getTime() <= Date.now()) {
      dispatch(stopImpersonation());
    }
  }, [active, expiresAt, tick, dispatch]);

  if (!active) return null;

  return (
    <Box
      role="alert"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar + 2,
        bgcolor: "error.main",
        color: "error.contrastText",
        px: 2,
        py: 1,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        boxShadow: 2,
      }}
    >
      <VisibilityIcon fontSize="small" />
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        Impersonating {targetBrandName ?? targetUserEmail ?? "brand"}
      </Typography>
      {targetUserEmail && (
        <Typography variant="body2" sx={{ opacity: 0.85 }}>
          ({targetUserEmail})
        </Typography>
      )}
      <Box sx={{ flexGrow: 1 }} />
      <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
        Exits in {formatRemaining(expiresAt)}
      </Typography>
      <Button
        size="small"
        variant="contained"
        color="inherit"
        onClick={() => dispatch(stopImpersonation())}
        sx={{
          color: "error.main",
          bgcolor: "common.white",
          fontWeight: 600,
          textTransform: "none",
          "&:hover": { bgcolor: "grey.100" },
        }}
      >
        Exit
      </Button>
    </Box>
  );
};

export default ImpersonationBanner;
